// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import React, {useLayoutEffect, useState} from 'react';

import throttle from 'lodash.throttle';

const DELAY = 150 as const;

type UseResizeObserver = (ref: React.RefObject<Element | null | undefined>) => [number?, number?];

/**
 * Observes an element's content-box dimensions and returns its current width
 * and height in pixels, updated reactively as the element resizes.
 *
 * Uses the browser's `ResizeObserver` API under the hood. Resize events are
 * throttled to at most once every 150 ms and then scheduled via
 * `requestAnimationFrame` to align state updates with the browser paint cycle,
 * avoiding layout thrash and excessive re-renders during rapid resizing.
 *
 * The observer is connected in `useLayoutEffect` so the initial dimensions are
 * captured synchronously before the first paint. When the component unmounts
 * or the ref changes, the observer is disconnected automatically.
 *
 * @param ref - A React ref attached to the element to observe. The element
 *   must be mounted when the effect runs; passing a ref with `current: null`
 *   simply means nothing is observed until the ref is populated.
 * @returns A tuple `[width, height]` in pixels (both `undefined` until the
 *   first observation fires).
 *
 * @example
 * ```tsx
 * function Box() {
 *   const ref = useRef<HTMLDivElement>(null);
 *   const [width, height] = useResizeObserver(ref);
 *   return <div ref={ref}>{width}×{height}</div>;
 * }
 * ```
 */
export const useResizeObserver: UseResizeObserver = ref => {
    const [width, setWidth] = useState<number>();
    const [height, setHeight] = useState<number>();
    useLayoutEffect(() => {
        let animationFrame = 0;
        const throttledResize = throttle(([element]) => {
            cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(() => {
                const {
                    width: entryWidth,
                    height: entryHeight,
                } = element.contentRect;
                setWidth(entryWidth);
                setHeight(entryHeight);
            });
        }, DELAY);
        const resizeObserver = new ResizeObserver(throttledResize);
        if (ref.current) {
            resizeObserver.observe(ref.current);
        }
        return () => {
            throttledResize.cancel();

            if (resizeObserver) {
                resizeObserver.disconnect();
            }

            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [ref.current]); // eslint-disable-line react-hooks/exhaustive-deps

    return [width, height];
};
